/**
 * URL screener for the channel Messaging tab.
 *
 * Mirrors the Rails `TextMessageUrlScreener` so users on the LITE / PLUS
 * tiers get inline feedback before they hit Submit. The Rails service is
 * the source of truth — this client-side check is defense in depth.
 *
 * The class is stateless and idempotent: `normalizeForUrlScreen` on an
 * already-normalized string is a no-op, and `containsUrlLike` has no side
 * effects.
 */

// Common TLDs we expect in real-world spam attempts. Kept intentionally
// small to limit false positives on innocuous text like "5 a.m.".
const COMMON_TLDS = [
  "com", "net", "org", "io", "co", "tech", "app", "dev", "info", "biz",
  "me", "tv", "us", "uk", "ca", "au", "de", "fr", "es", "nl", "cn", "jp",
  "ru", "br", "in", "mx", "ai", "gov", "edu", "xyz", "online", "site",
  "shop", "store", "club", "live", "photo", "photos", "pics", "media",
  "studio", "agency", "design", "link", "click", "run", "pro", "plus",
];

const TLD_ALT = COMMON_TLDS.join("|");

// U+00B7, U+2022, U+30FB, U+02D9, U+2027, U+2219
const DOT_LOOKALIKES = ["·", "•", "・", "˙", "‧", "∙"];

// Bracketed dot forms are unconditional ("site [dot] com"). Bare " dot "
// is intentionally NOT substituted in normalize because several
// photography/event TLDs ("design", "shop", "studio", etc.) are also
// ordinary English words ("polka dot design"). Real bare-word
// obfuscation is detected separately in containsUrlLike.
const DOT_WORD_BRACKETED = /\s*[(\[{]\s*dot\s*[)\]}]\s*/gi;

// Bracketed forms of "at" are unconditional ("user [at] site"). The bare
// word " at " is too common in ordinary copy ("event starts at 8pm") to
// substitute globally — we only apply it after a confident TLD signal is
// present in the string.
const AT_WORD_BRACKETED = /\s*[(\[{]\s*at\s*[)\]}]\s*/gi;
const AT_WORD_BARE = /\s+at\s+/gi;

const SCHEME_RE = /\b(?:https?|ftp):\/\//i;
// Email-like is gated by a known TLD so ordinary sentences ("starts at
// 8pm. Thank you") don't get caught by the bare-word at-substitution.
const EMAIL_RE = new RegExp(
  `[a-z0-9._%+\\-]+@[a-z0-9.\\-]+\\.(?:${TLD_ALT})\\b`,
  "i",
);
const DOMAIN_RE = new RegExp(
  `(?:\\bwww\\.)?[a-z0-9](?:[a-z0-9\\-]{0,62}[a-z0-9])?\\.(?:${TLD_ALT})\\b`,
  "i",
);
const TLD_PRESENCE_RE = new RegExp(`\\.(?:${TLD_ALT})\\b`, "i");

// TLDs that double as ordinary English words — exclude from the
// bare-word " dot " obfuscation detector to avoid photography-prose
// false positives ("polka dot design").
const AMBIGUOUS_BARE_DOT_TLDS = new Set([
  "me", "run", "pro", "plus", "live", "shop", "store", "club", "design",
  "photo", "photos", "pics", "media", "studio", "agency", "online",
  "site", "link", "click", "in",
]);
const UNAMBIGUOUS_BARE_DOT_TLDS = COMMON_TLDS.filter(
  (t) => !AMBIGUOUS_BARE_DOT_TLDS.has(t),
);

// Detect bare-word " dot " obfuscation conservatively: require the
// preceding label to be 4+ chars and the trailing TLD to be one we
// know is unambiguous. Catches "visit mywebsite dot com" without
// flagging "we do dot net work" or "polka dot design".
const DOT_WORD_OBFUSCATION_RE = new RegExp(
  `\\b[a-z0-9][a-z0-9\\-]{3,62}\\s+dot\\s+(?:${UNAMBIGUOUS_BARE_DOT_TLDS.join("|")})\\b`,
  "i",
);
// Same shape but with the TLD letters spaced out: "site dot c o m".
const DOT_WORD_SPACED_TLD_RE = new RegExp(
  `\\b[a-z0-9][a-z0-9\\-]{3,62}\\s+dot\\s+(?:${UNAMBIGUOUS_BARE_DOT_TLDS.map((t) => t.split("").join("\\s+")).join("|")})\\b`,
  "i",
);

const RESTRICTED_TIERS = new Set(["lite", "plus"]);

export const URL_BLOCK_MESSAGE =
  "URLs and link-like content are not allowed on the LITE and PLUS plans.";

export function normalizeForUrlScreen(text) {
  if (text === null || text === undefined) return "";
  let s = String(text).toLowerCase();

  // Unicode dot lookalikes → ASCII dot
  for (const c of DOT_LOOKALIKES) {
    s = s.split(c).join(".");
  }

  // Bracketed at/dot forms are unconditional obfuscation.
  s = s.replace(AT_WORD_BRACKETED, "@");
  s = s.replace(DOT_WORD_BRACKETED, ".");

  // Bare " at " is only safe to collapse if the string still contains a
  // recognizable TLD — that prevents "starts at 8pm. Thank you" from
  // turning into a fake email shape.
  if (s.includes(".") && TLD_PRESENCE_RE.test(s)) {
    s = s.replace(AT_WORD_BARE, "@");
  }

  // Spaces around the dot: "site . com" → "site.com". Require
  // whitespace on *both* sides so sentence-ending periods don't
  // collapse into TLD shapes ("Thank you. Me too!" → "you.me").
  s = s.replace(/\s+\.\s+/g, ".");

  // Spaced-out TLDs: "site.c o m" → "site.com"
  s = s.replace(/\.([a-z])(?:\s+([a-z])){1,5}\b/g, (match) =>
    "." + match.replace(/[. ]/g, ""),
  );

  // Spaces around "@"
  s = s.replace(/\s*@\s*/g, "@");

  return s;
}

export function containsUrlLike(text) {
  if (text === null || text === undefined) return false;
  const trimmed = String(text).trim();
  if (trimmed === "") return false;

  const normalized = normalizeForUrlScreen(text);
  if (SCHEME_RE.test(normalized)) return true;
  if (EMAIL_RE.test(normalized)) return true;
  if (DOMAIN_RE.test(normalized)) return true;
  if (DOT_WORD_OBFUSCATION_RE.test(normalized)) return true;
  if (DOT_WORD_SPACED_TLD_RE.test(normalized)) return true;
  return false;
}

export function isRestrictedTier(tier) {
  if (!tier) return false;
  return RESTRICTED_TIERS.has(String(tier).toLowerCase());
}

export const RESTRICTED_MESSAGING_TIERS = Array.from(RESTRICTED_TIERS);
